(() => {
  const root = document.querySelector("[data-al-localization-root]");
  if (!root) return;

  const modal = root.querySelector("[data-al-localization-modal]");
  const dialog = root.querySelector("[role='dialog']");
  const form = root.querySelector("[data-al-localization-form]");
  const dataNode = root.querySelector("[data-al-localization-data]");
  const copyNode = root.querySelector("[data-al-localization-copy]");
  if (!modal || !dialog || !form || !dataNode || !copyNode) return;

  let data;
  let translations;
  try {
    data = JSON.parse(dataNode.textContent);
    translations = JSON.parse(copyNode.textContent);
  } catch (error) {
    console.warn("Artem localization data unavailable", error);
    return;
  }

  // Keep this safety-critical selector readable from every storefront locale.
  // The selected storefront language still applies after submission; only the
  // selector interface itself is intentionally always displayed in English.
  const copyKey = "en";
  const copy = translations.en || {};
  const copyFor = (key) => copy[key] || translations.en?.[key] || "";
  const pageLocale = String(document.documentElement.lang || data.current.language || "en").replace("_", "-");
  const translationKeyFor = (locale) => {
    const normalized = String(locale || "").replace("_", "-");
    const exact = Object.keys(translations).find(
      (key) => key.toLowerCase() === normalized.toLowerCase(),
    );
    if (exact) return exact;
    const language = normalized.split("-")[0].toLowerCase();
    return Object.keys(translations).find((key) => key.toLowerCase() === language) || "en";
  };
  const pageCopy = translations[translationKeyFor(pageLocale)] || translations.en || {};
  const pageCopyFor = (key) => pageCopy[key] || translations.en?.[key] || "";

  const countries = Array.isArray(data.countries) ? data.countries : [];
  const countryInput = form.querySelector("[data-al-country-input]");
  const languageInput = form.querySelector("[data-al-language-input]");
  const countrySearch = form.querySelector("[data-al-country-search]");
  const countryResults = form.querySelector("[data-al-country-results]");
  const countryEmpty = form.querySelector("[data-al-country-empty]");
  const languageResults = form.querySelector("[data-al-language-results]");
  const countrySummary = form.querySelector("[data-al-country-summary]");
  const finalSummary = form.querySelector("[data-al-final-summary]");
  const currencyValue = form.querySelector("[data-al-currency-value]");
  const backButton = form.querySelector("[data-al-back]");
  const nextButton = form.querySelector("[data-al-next]");
  const applyButton = form.querySelector("[data-al-apply]");
  const recommendation = root.querySelector("[data-al-recommendation]");
  const recommendationCopy = root.querySelector("[data-al-recommendation-copy]");
  const recommendationFlag = root.querySelector("[data-al-recommendation-flag]");
  const recommendationStay = root.querySelector("[data-al-recommendation-stay]");
  const recommendationChoose = root.querySelector("[data-al-recommendation-choose]");
  const selectorLocale = "en";
  const collator = new Intl.Collator(selectorLocale, {
    sensitivity: "base",
  });
  const regionNames =
    typeof Intl.DisplayNames === "function"
      ? new Intl.DisplayNames([selectorLocale], { type: "region" })
      : null;
  const languageNames =
    typeof Intl.DisplayNames === "function"
      ? new Intl.DisplayNames([selectorLocale], { type: "language" })
      : null;

  let activeStep = 1;
  let selectedCountry =
    countries.find((country) => country.code === data.current.country) || countries[0];
  let selectedLanguage = data.current.language;
  let returnFocus = null;
  let suggestedCountry = null;

  const interpolate = (template, values) =>
    Object.entries(values).reduce(
      (copy, [key, value]) => copy.replaceAll(`__${key}__`, value || ""),
      template || "",
    );

  const countryName = (country) => {
    try {
      return regionNames?.of(country.code) || country.name;
    } catch (_) {
      return country.name;
    }
  };

  const languageName = (language) => {
    const code = String(language?.code || "").replace("_", "-");
    try {
      return languageNames?.of(code) || language.name || code.toUpperCase();
    } catch (_) {
      return language.name || code.toUpperCase();
    }
  };

  const setText = (selector, key, scope = root) => {
    const element = scope.querySelector(selector);
    if (element) element.textContent = copyFor(key);
  };

  const setAttribute = (selector, attribute, key, scope = root) => {
    const element = scope.querySelector(selector);
    if (element) element.setAttribute(attribute, copyFor(key));
  };

  const applyTranslations = () => {
    root.querySelectorAll("[data-al-localization-close]").forEach((element) =>
      element.setAttribute("aria-label", copyFor("close")),
    );
    const currentCountry = countries.find((country) => country.code === data.current.country);
    const currentLanguage = currentCountry?.languages?.find(
      (language) => language.code === data.current.language,
    ) || { code: data.current.language, name: data.current.language };
    document.querySelectorAll("[data-al-localization-open]").forEach((element) => {
      element.setAttribute("aria-label", copyFor("open_selector"));
      const fullLabel = element.querySelector(".al-localization-trigger__full");
      if (fullLabel && currentCountry) {
        fullLabel.textContent = `${countryName(currentCountry)} · ${languageName(currentLanguage)} · ${currentCountry.currencyCode} ${currentCountry.currencySymbol}`.trim();
      }
    });

    setText(".al-localization__eyebrow", "eyebrow");
    setText("#AlLocalizationTitle", "title");
    setText("#AlLocalizationIntroduction", "introduction");
    setAttribute(".al-localization__steps", "aria-label", "steps_label");

    ["step_country", "step_language", "step_currency"].forEach((key, index) => {
      const indicator = root.querySelector(`[data-al-step-indicator='${index + 1}']`);
      if (!indicator) return;
      const number = indicator.querySelector("span");
      indicator.replaceChildren(number || document.createElement("span"), document.createTextNode(copyFor(key)));
      indicator.querySelector("span").textContent = String(index + 1);
    });

    setText("[data-al-step='1'] h3", "country_heading");
    setText(".al-localization__search-label", "search_label");
    setAttribute("[data-al-country-search]", "placeholder", "search_placeholder");
    setAttribute("[data-al-country-results]", "aria-label", "country_results");
    setText("[data-al-country-empty]", "no_country");
    setText("[data-al-step='2'] h3", "language_heading");
    setAttribute("[data-al-language-results]", "aria-label", "language_results");
    setText("[data-al-step='3'] h3", "currency_heading");
    setText(".al-localization__currency-label", "currency_locked");
    setText(".al-localization__currency-note", "currency_note");
    setText("[data-al-back]", "back");
    setText("[data-al-next]", "continue");
    setText("[data-al-apply]", "apply");
    setText("[data-al-recommendation-stay]", "stay");
    setText("[data-al-recommendation-choose]", "choose_preferences");
    recommendationCopy.dataset.template = copyFor("recommendation");

    data.labels.selected = copyFor("selected");
    data.labels.countryCurrency = copyFor("country_currency");
    data.labels.countryLanguage = copyFor("country_language");

    document.querySelectorAll(".al-market-cart-notice").forEach((notice) => {
      const heading = notice.querySelector("strong");
      const explanation = notice.querySelector("p + p");
      const action = notice.querySelector("button");
      if (heading && currentCountry) {
        heading.textContent = interpolate(pageCopyFor("cart_market"), {
          COUNTRY: currentCountry.name,
          CURRENCY: currentCountry.currencyCode,
        });
      }
      if (explanation) explanation.textContent = pageCopyFor("cart_recalculation");
      if (action) action.textContent = pageCopyFor("cart_action");
    });
  };

  const normalize = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase(selectorLocale)
      .trim();

  const flag = (country) => {
    const icon = document.createElement("span");
    icon.className = `flag-icon flag-icon-${country.code.toLowerCase()}`;
    icon.setAttribute("aria-hidden", "true");
    return icon;
  };

  const currentLanguages = () => {
    const available = selectedCountry?.languages || [];
    return available.length
      ? available
      : [{ code: data.current.language, name: data.current.language.toUpperCase() }];
  };

  const ensureLanguage = () => {
    const available = currentLanguages();
    if (!available.some((language) => language.code === selectedLanguage)) {
      selectedLanguage = available[0]?.code || data.current.language;
    }
    languageInput.value = selectedLanguage;
  };

  const chooseCountry = (country) => {
    selectedCountry = country;
    countryInput.value = country.code;
    ensureLanguage();
    renderCountries(countrySearch.value);
  };

  const renderCountries = (query = "") => {
    const needle = normalize(query);
    const matches = countries
      .filter((country) =>
        normalize(`${countryName(country)} ${country.name} ${country.code} ${country.continent}`).includes(needle),
      )
      .sort((a, b) => collator.compare(countryName(a), countryName(b)));

    countryResults.replaceChildren();
    countryEmpty.hidden = matches.length > 0;
    matches.forEach((country) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "al-localization__country";
      button.dataset.countryCode = country.code;
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(country.code === selectedCountry?.code));
      button.append(flag(country));

      const name = document.createElement("span");
      name.className = "al-localization__country-name";
      name.textContent = countryName(country);
      button.append(name);

      const currency = document.createElement("span");
      currency.className = "al-localization__country-currency";
      currency.textContent = `${country.currencyCode} ${country.currencySymbol}`.trim();
      button.append(currency);

      if (country.code === selectedCountry?.code) {
        const selected = document.createElement("span");
        selected.className = "visually-hidden";
        selected.textContent = data.labels.selected;
        button.append(selected);
      }

      button.addEventListener("click", () => chooseCountry(country));
      countryResults.append(button);
    });
  };

  const renderLanguages = () => {
    ensureLanguage();
    languageResults.replaceChildren();
    countrySummary.textContent = interpolate(data.labels.countryCurrency, {
      COUNTRY: countryName(selectedCountry),
      CURRENCY: `${selectedCountry.currencyCode} ${selectedCountry.currencySymbol}`.trim(),
    });

    currentLanguages().forEach((language) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "al-localization__language";
      button.dataset.languageCode = language.code;
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", String(language.code === selectedLanguage));
      button.textContent = languageName(language);
      button.addEventListener("click", () => {
        selectedLanguage = language.code;
        languageInput.value = language.code;
        renderLanguages();
      });
      languageResults.append(button);
    });
  };

  const renderCurrency = () => {
    const language =
      currentLanguages().find((item) => item.code === selectedLanguage) || currentLanguages()[0];
    finalSummary.textContent = interpolate(data.labels.countryLanguage, {
      COUNTRY: countryName(selectedCountry),
      LANGUAGE: languageName(language),
    });
    currencyValue.textContent =
      `${selectedCountry.currencyCode} ${selectedCountry.currencySymbol}`.trim();
  };

  const showStep = (step) => {
    activeStep = Math.max(1, Math.min(3, step));
    form.querySelectorAll("[data-al-step]").forEach((panel) => {
      panel.hidden = Number(panel.dataset.alStep) !== activeStep;
    });
    root.querySelectorAll("[data-al-step-indicator]").forEach((indicator) => {
      const number = Number(indicator.dataset.alStepIndicator);
      indicator.dataset.state = number === activeStep ? "active" : number < activeStep ? "complete" : "future";
      if (number === activeStep) indicator.setAttribute("aria-current", "step");
      else indicator.removeAttribute("aria-current");
    });
    backButton.hidden = activeStep === 1;
    nextButton.hidden = activeStep === 3;
    applyButton.hidden = activeStep !== 3;
    if (activeStep === 2) renderLanguages();
    if (activeStep === 3) renderCurrency();
  };

  const focusable = () =>
    Array.from(
      dialog.querySelectorAll(
        "button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), [href], [tabindex]:not([tabindex='-1'])",
      ),
    ).filter((element) => element.getClientRects().length > 0);

  const open = (countryCode) => {
    const requested = countries.find((country) => country.code === countryCode);
    if (requested) chooseCountry(requested);
    else chooseCountry(selectedCountry);
    recommendation.hidden = true;
    returnFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add("al-localization-open");
    showStep(1);
    countrySearch.value = "";
    renderCountries();
    window.requestAnimationFrame(() => countrySearch.focus());
  };

  const close = () => {
    modal.hidden = true;
    document.documentElement.classList.remove("al-localization-open");
    if (returnFocus && typeof returnFocus.focus === "function") returnFocus.focus();
  };

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-al-localization-open]");
    if (!trigger) return;
    event.preventDefault();
    open(trigger.dataset.countryCode);
  });

  root.querySelectorAll("[data-al-localization-close]").forEach((button) =>
    button.addEventListener("click", close),
  );
  countrySearch.addEventListener("input", () => renderCountries(countrySearch.value));
  nextButton.addEventListener("click", () => showStep(activeStep + 1));
  backButton.addEventListener("click", () => showStep(activeStep - 1));
  form.addEventListener("submit", () => {
    applyButton.disabled = true;
    form.setAttribute("aria-busy", "true");
    try {
      sessionStorage.setItem("al_localization_changed", "1");
    } catch (_) {}
  });

  document.addEventListener("keydown", (event) => {
    if (modal.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const elements = focusable();
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const recommendationKey = "al_localization_recommendation_v1";
  const rememberRecommendation = (detectedCode) => {
    try {
      localStorage.setItem(
        recommendationKey,
        JSON.stringify({ detectedCode, currentCode: data.current.country, at: Date.now() }),
      );
    } catch (_) {}
  };

  const recommendationWasHandled = (detectedCode) => {
    try {
      const saved = JSON.parse(localStorage.getItem(recommendationKey) || "null");
      return (
        saved?.detectedCode === detectedCode &&
        saved?.currentCode === data.current.country &&
        Date.now() - Number(saved.at || 0) < 90 * 24 * 60 * 60 * 1000
      );
    } catch (_) {
      return false;
    }
  };

  const anotherOverlayIsVisible = () =>
    Array.from(
      document.querySelectorAll(
        "dialog[open], [aria-modal='true']:not([hidden]), .modal.active, .shopify-pc__banner__dialog, #shopify-pc__banner:not([hidden])",
      ),
    ).some((element) => !root.contains(element) && element.getClientRects().length > 0);

  const displayRecommendation = (country, attempt = 0) => {
    if (anotherOverlayIsVisible()) {
      if (attempt < 15) window.setTimeout(() => displayRecommendation(country, attempt + 1), 2000);
      return;
    }
    suggestedCountry = country;
    recommendationCopy.textContent = interpolate(recommendationCopy.dataset.template, {
      COUNTRY: countryName(country),
    });
    recommendationFlag.className =
      `al-localization__recommendation-flag flag-icon flag-icon-${country.code.toLowerCase()}`;
    recommendation.hidden = false;
  };

  recommendationStay.addEventListener("click", () => {
    if (suggestedCountry) rememberRecommendation(suggestedCountry.code);
    recommendation.hidden = true;
  });
  recommendationChoose.addEventListener("click", () => {
    if (suggestedCountry) rememberRecommendation(suggestedCountry.code);
    open(suggestedCountry?.code);
  });

  const discoverLocalization = async () => {
    if (data.designMode || countries.length < 2) return;
    try {
      const url =
        `${data.routesRoot}browsing_context_suggestions.json` +
        `?country[enabled]=true&country[exclude]=${encodeURIComponent(data.current.country)}` +
        `&language[enabled]=true&language[exclude]=${encodeURIComponent(data.current.language)}`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const payload = await response.json();
      const detectedCode = payload.detected_values?.country?.handle;
      if (!detectedCode || detectedCode === data.current.country) return;
      const country = countries.find((item) => item.code === detectedCode);
      if (!country || recommendationWasHandled(detectedCode)) return;
      displayRecommendation(country);
    } catch (_) {}
  };

  chooseCountry(selectedCountry);
  applyTranslations();
  showStep(1);
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(discoverLocalization, { timeout: 3500 });
  } else {
    window.setTimeout(discoverLocalization, 2200);
  }
})();
